/**
 * EmployeeSettings Component
 * 
 * Settings page component for employees to manage their account.
 * Features:
 * - Display of employee personal information (read-only)
 * - Password change functionality with validation
 * - Modal dialog for password change
 * - Form validation with error handling
 * 
 * @component
 */

import React, { useState } from 'react';
import './EmployeeSettings.css';
import EmployeeService from '../../services/EmployeeService';

/**
 * Props interface for EmployeeSettings component
 */
interface EmployeeSettingsProps {
  currentEmployee: {
    empCode: string;   // Employee code
    name: string;      // Employee name
    userType: string;  // User type (e.g., 'employee')
  };
}

export const EmployeeSettings: React.FC<EmployeeSettingsProps> = ({ currentEmployee }) => {
  // State for managing password change modal visibility
  const [isPwdModalOpen, setIsPwdModalOpen] = useState(false);
  
  // State for password form fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // State for form validation errors
  const [errors, setErrors] = useState<{
    currentPassword?: string;  // Error message for current password field
    newPassword?: string;      // Error message for new password field
    confirmPassword?: string;  // Error message for confirm password field
    form?: string;             // General form error message
  }>({});

  /**
   * Resets all password form fields to empty strings.
   * Called when modal is closed or after successful password change.
   */
  const resetForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  /**
   * Opens the password change modal dialog.
   */
  const openModal = () => setIsPwdModalOpen(true);
  
  /**
   * Closes the password change modal, resets form fields, and clears errors.
   */
  const closeModal = () => { setIsPwdModalOpen(false); resetForm(); setErrors({}); };

  /**
   * Handles password update form submission.
   * Performs client-side validation before sending request to server.
   * 
   * Validation rules:
   * - Password must contain at least one uppercase letter and one number
   * - Password must be alphanumeric only
   * - Password must be at least 6 characters long
   * - New password and confirm password must match
   * 
   * @param e - Form submission event
   */
  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    // Basic client-side validation checks
    const passwordRegex = /^(?=.*[A-Z])(?=.*\d)[A-Za-z0-9]+$/;
    const newErrors: { currentPassword?: string; newPassword?: string; confirmPassword?: string; form?: string } = {};
    
    // Validate password format: must contain at least one uppercase letter and one number, alphanumeric only
    if (!passwordRegex.test(newPassword)) {
      newErrors.newPassword = '英数字のみ。少なくとも1つの大文字と数字を含めてください';
    }
    
    // Validate password minimum length
    if (newPassword.length < 6) {
      newErrors.newPassword = '6文字以上である必要があります';
    }
    
    // Validate password confirmation match
    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = '新しいパスワードが一致しません';
    }
    
    // If validation errors exist, display them and stop submission
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Submit password change request to server
    try {
      const res = await EmployeeService.changePassword(currentEmployee.empCode, currentPassword, newPassword);
      
      if (res.success) {
        // Close modal on successful password change
        closeModal();
      } else {
        // Handle different error types from server response
        const msg = res.message || '';
        if (msg.includes('Current password is incorrect')) {
          setErrors({ currentPassword: '現在のパスワードが正しくありません' });
        } else if (msg.match(/password/i)) {
          setErrors({ newPassword: 'パスワード要件を満たしていません' });
        } else {
          setErrors({ form: 'パスワードの変更に失敗しました' });
        }
      }
    } catch (err) {
      // Handle network or unexpected errors
      setErrors({ form: 'パスワードの変更中にエラーが発生しました' });
    }
  };

  /**
   * Handler for current password input changes.
   * Updates state and clears relevant errors when user types.
   * 
   * @param v - New value from input field
   */
  const onChangeCurrent = (v: string) => {
    setCurrentPassword(v);
    if (errors.currentPassword) setErrors(prev => ({ ...prev, currentPassword: undefined }));
    if (errors.form) setErrors(prev => ({ ...prev, form: undefined }));
  };
  
  /**
   * Handler for new password input changes.
   * Updates state and clears relevant errors when user types.
   * 
   * @param v - New value from input field
   */
  const onChangeNew = (v: string) => {
    setNewPassword(v);
    if (errors.newPassword) setErrors(prev => ({ ...prev, newPassword: undefined }));
    if (errors.form) setErrors(prev => ({ ...prev, form: undefined }));
  };
  
  /**
   * Handler for confirm password input changes.
   * Updates state and clears relevant errors when user types.
   * 
   * @param v - New value from input field
   */
  const onChangeConfirm = (v: string) => {
    setConfirmPassword(v);
    if (errors.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: undefined }));
    if (errors.form) setErrors(prev => ({ ...prev, form: undefined }));
  };
  return (
    <div className="employee-settings">
      {/* Settings page header */}
      <div className="settings-header">
        <h2>⚙️ 設定</h2>
        <p>アカウント設定と個人情報</p>
      </div>
      
      <div className="settings-content">
        {/* Personal Information Section - displays read-only employee information */}
        <div className="settings-section">
          <h3>👤 個人情報</h3>
          <div className="info-grid">
            <div className="info-item">
              <label>従業員コード:</label>
              <span>{currentEmployee.empCode}</span>
            </div>
            <div className="info-item">
              <label>氏名:</label>
              <span>{currentEmployee.name}</span>
            </div>
            <div className="info-item">
              <label>ユーザータイプ:</label>
              <span>{currentEmployee.userType}</span>
            </div>
          </div>
        </div>
        
        {/* Security Section - password change functionality */}
        <div className="settings-section">
          <h3>🔒 セキュリティ</h3>
          <div className="security-info">
            <p>パスワードを変更できます。</p>
            <button className="change-password-btn" onClick={openModal}>パスワードを変更</button>
          </div>
        </div>
        
        {/* System Information Section - displays system version and login info */}
        <div className="settings-section">
          <h3>📱 システム情報</h3>
          <div className="system-info">
            <p>給与明細システム v1.0</p>
            <p>最終ログイン: {new Date().toLocaleString('ja-JP')}</p>
          </div>
        </div>
      </div>

      {/* Password Change Modal - displayed when isPwdModalOpen is true */}
      {isPwdModalOpen && (
        <div className="pwd-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="changePwdTitle">
          <div className="pwd-modal">
            {/* Modal header with title and close button */}
            <div className="pwd-modal-header">
              <h3 id="changePwdTitle">🔒 パスワード変更</h3>
              <button className="pwd-modal-close" onClick={closeModal} aria-label="閉じる">✕</button>
            </div>
            <div className="pwd-modal-body">
              {/* Employee information display (read-only) */}
              <div className="info-row">
                <div>
                  <label>従業員コード</label>
                  <span className="readonly-pill">{currentEmployee.empCode}</span>
                </div>
                <div>
                  <label>氏名</label>
                  <span className="readonly-pill">{currentEmployee.name}</span>
                </div>
              </div>
              
              {/* Password change form */}
              <form onSubmit={handleUpdatePassword} className="pwd-form">
                {/* General form error message */}
                {errors.form && (
                  <div className="form-error" role="alert">{errors.form}</div>
                )}
                
                {/* Current password input field */}
                <div className="form-field">
                  <label htmlFor="currentPassword">現在のパスワード</label>
                  <input
                    id="currentPassword"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => onChangeCurrent(e.target.value)}
                    placeholder="現在のパスワードを入力"
                    required
                    className={errors.currentPassword ? 'input-error' : ''}
                  />
                  {errors.currentPassword && (<span className="field-error">{errors.currentPassword}</span>)}
                </div>
                
                {/* New password input field */}
                <div className="form-field">
                  <label htmlFor="newPassword">新しいパスワード</label>
                  <input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => onChangeNew(e.target.value)}
                    placeholder="新しいパスワードを入力"
                    required
                    className={errors.newPassword ? 'input-error' : ''}
                  />
                  {errors.newPassword && (<span className="field-error">{errors.newPassword}</span>)}
                </div>
                
                {/* Confirm password input field */}
                <div className="form-field">
                  <label htmlFor="confirmPassword">新しいパスワード（確認）</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => onChangeConfirm(e.target.value)}
                    placeholder="新しいパスワードを再入力"
                    required
                    className={errors.confirmPassword ? 'input-error' : ''}
                  />
                  {errors.confirmPassword && (<span className="field-error">{errors.confirmPassword}</span>)}
                </div>
                
                {/* Form action buttons */}
                <div className="pwd-modal-actions">
                  <button type="button" className="btn-secondary" onClick={closeModal}>キャンセル</button>
                  <button type="submit" className="btn-primary">更新</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
